import './FeatureFlagManager.scss';

import { ADMIN_DASHBOARD_COPY } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { objectKeys } from '@sonar-software/sonar-ts-lib';
import omitBy from 'lodash/omitBy';
import pickBy from 'lodash/pickBy';

import { adminActions, ShardSearchEntryDetails } from '@sonar-software/sonar-admin';
import { Checkbox, Input, Tooltip } from 'antd';
import { coreActions, FeatureFlagDictionary, FeatureFlagLabel } from '@sonar-software/sonar-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAction, useSonarSelector } from '@hooks';

const getCustomerFeatureFlagsSelectors = createAsyncSelectors((state) => state.core.customers.byId.featureFlags.get);

const searchableFeatureFlags = objectKeys(FeatureFlagLabel).reduce((obj, key) => {
	obj[ key ] = FeatureFlagLabel[ key ].toLocaleLowerCase();

	return obj;
}, {} as Record<string, string>);

export interface FeatureFlagManagerProps {
	id?: string;
	searchDetails?: ShardSearchEntryDetails;
}

export const FeatureFlagManager: React.FC<FeatureFlagManagerProps> = (props) => {
	const { id, searchDetails } = props;

	const [ featureFlagSelections, setFeatureFlagSelections ] = useState<string[]>([]);
	const [ featureFlagFilter, setFeatureFlagFilter ] = useState<string>('');

	const getCustomerFeatureFlags = useAction(coreActions.requestGetCoreCustomersByIdFeatureFlags);

	const customerFeatureFlags = useSonarSelector(getCustomerFeatureFlagsSelectors.data);
	const customerFeatureFlagOptions = useMemo(() => {
		if (!customerFeatureFlags) {
			setFeatureFlagSelections([]);

			return [];
		}

		const displayedFilter = featureFlagFilter.toLocaleLowerCase();
		const displayed = featureFlagFilter ?
			omitBy(
				customerFeatureFlags,
				(value, key) => !(searchableFeatureFlags[ key ] ?? key.toLocaleLowerCase()).includes(displayedFilter)
			  ) :
			customerFeatureFlags;

		const selections = Object.keys(pickBy(displayed, (value) => value));
		setFeatureFlagSelections(selections);

		return Object.keys(displayed).map((key) => ({
			checked: customerFeatureFlags[ key as keyof FeatureFlagDictionary ],
			label: <Tooltip title={ key }>{ (FeatureFlagLabel as Record<string, string>)[ key ] ?? key }</Tooltip>,
			value: key
		}));
	}, [ customerFeatureFlags, setFeatureFlagSelections, featureFlagFilter ]);

	const handleSearchChanged = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFeatureFlagFilter(e.target?.value);
		},
		[ setFeatureFlagFilter ]
	);

	const updateFeatureFlags = useAction(adminActions.requestPostAdminCustomersFeatureFlags);
	const handleCheckboxChanged = useCallback(
		(checkedValue: string[]) => {
			if (searchDetails) {
				const { customerId, shardName } = searchDetails;

				const selections = checkedValue.map((v) => v.toString());
				setFeatureFlagSelections(selections);

				updateFeatureFlags({
					customerId: customerId,
					flags: selections,
					shardName: shardName
				});
			}
		},
		[ setFeatureFlagSelections, updateFeatureFlags, searchDetails ]
	);

	useEffect(() => {
		if (searchDetails) {
			const { customerId, shardName } = searchDetails;

			getCustomerFeatureFlags({
				customerId: customerId,
				shardName
			});
		}
	}, [ searchDetails, getCustomerFeatureFlags ]);

	return (
		<div
			className='feature-flag-manager'
			id={ id }>
			<div className='h2'>{ ADMIN_DASHBOARD_COPY.FEATURE_FLAGS }</div>
			<Input.Search
				allowClear
				className='feature-flag-search'
				enterButton
				onChange={ handleSearchChanged }
				placeholder={ ADMIN_DASHBOARD_COPY.SEARCH_FEATURE_FLAGS_PLACEHOLDER } />
			<Checkbox.Group
				onChange={ handleCheckboxChanged }
				options={ customerFeatureFlagOptions }
				value={ featureFlagSelections } />
		</div>
	);
};
